import { Link } from "react-router-dom";
import { ShieldCheck, Network, BookOpen, ArrowRight } from "lucide-react";
import Seo from "../lib/Seo";
import SocialLinks from "../components/SocialLinks";

export default function Nosotros() {
    return (
        <>
            <Seo
                title="Quiénes somos - Centro de Rehabilitación de Drogas Chile"
                description="Somos un directorio editorial independiente enfocado en centros de rehabilitación de drogas en Chile, parte de la red SinAdicciones.org."
                path="/nosotros"
                keywords="quiénes somos, directorio rehabilitación Chile, SinAdicciones"
            />

            <section className="border-b border-slate-800 bg-slate-950">
                <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
                    <h1 className="text-3xl font-bold tracking-tight text-slate-50 sm:text-5xl">
                        Quiénes somos
                    </h1>
                    <p className="mt-4 text-lg text-slate-400">
                        centroderehabilitaciondedrogas.cl es un sitio editorial
                        independiente enfocado exclusivamente en{" "}
                        <strong className="text-cyan-400">
                            centros de rehabilitación de drogas en Chile
                        </strong>
                        . Curamos información pública, comparamos opciones y ayudamos
                        a pacientes y familias a tomar decisiones clínicas
                        informadas.
                    </p>
                </div>
            </section>

            <section className="py-14">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <div className="grid gap-6 md:grid-cols-3">
                        {[
                            {
                                icon: ShieldCheck,
                                title: "Enfoque editorial",
                                text: "Contenido único, clínico y verificado. No promocionamos sin evidencia.",
                            },
                            {
                                icon: Network,
                                title: "Red SinAdicciones",
                                text: "Somos parte de la red SinAdicciones.org, la plataforma más completa de Chile.",
                            },
                            {
                                icon: BookOpen,
                                title: "Educación pública",
                                text: "Blog con guías, costos y modalidades accesible para todos.",
                            },
                        ].map((b) => (
                            <div
                                key={b.title}
                                className="rounded-xl border border-slate-800 bg-slate-900 p-6"
                            >
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-400">
                                    <b.icon className="h-5 w-5" />
                                </div>
                                <h2 className="mt-4 text-base font-semibold text-slate-50">
                                    {b.title}
                                </h2>
                                <p className="mt-2 text-sm text-slate-400">{b.text}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-14 space-y-6 text-slate-300">
                        <div>
                            <h2 className="text-2xl font-bold text-slate-50">
                                Nuestra misión
                            </h2>
                            <p className="mt-3 leading-relaxed">
                                Entregar información clara sobre centros de
                                rehabilitación de drogas para que ningún paciente o
                                familia tome decisiones a ciegas. Creemos que un
                                directorio transparente reduce tiempos de búsqueda,
                                mejora la adherencia al tratamiento y contribuye a la
                                salud pública.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-slate-50">
                                Parte de la red SinAdicciones.org
                            </h2>
                            <p className="mt-3 leading-relaxed">
                                Toda la información de centros y profesionales
                                proviene de{" "}
                                <a
                                    href="https://sinadicciones.org"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-cyan-400 hover:text-cyan-300"
                                >
                                    SinAdicciones.org
                                </a>
                                , la plataforma chilena que concentra centros,
                                profesionales, talleres y herramientas para la
                                rehabilitación integral. Si eres centro o
                                profesional, te recomendamos registrarte en la
                                plataforma principal.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-slate-50">
                                Transparencia y límites
                            </h2>
                            <p className="mt-3 leading-relaxed">
                                Este sitio no reemplaza atención médica. En
                                situaciones de riesgo vital llama al SAMU 131. La
                                información publicada tiene fines educativos y no
                                constituye una recomendación clínica personalizada.
                            </p>
                        </div>
                    </div>

                    <div className="mt-12 flex flex-wrap gap-4">
                        <Link
                            to="/centros"
                            data-testid="nosotros-cta-centros"
                            className="inline-flex items-center gap-1 rounded-md bg-cyan-500 px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-cyan-400"
                        >
                            Ver directorio <ArrowRight className="h-4 w-4" />
                        </Link>
                        <a
                            href="https://sinadicciones.org"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="nosotros-cta-sinadicciones"
                            className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-transparent px-5 py-2.5 text-sm font-semibold text-slate-100 hover:bg-slate-800"
                        >
                            Visitar SinAdicciones.org
                        </a>
                    </div>

                    <div className="mt-14 border-t border-slate-800 pt-10">
                        <h2 className="text-2xl font-bold text-slate-50">
                            Síguenos en redes sociales
                        </h2>
                        <p className="mt-3 text-slate-400">
                            Encuentra contenido diario sobre rehabilitación,
                            testimonios y recursos en las redes de SinAdicciones.org.
                        </p>
                        <div className="mt-6">
                            <SocialLinks variant="large" />
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
